import { useEffect, useState } from "react";
import axios from "axios";

/**
 * usePeerBenchmarks
 *
 * Fetches the tenant's active FRB (Financial Ratio Benchmark) comparison via
 * `/api/benchmarking/comparison`. Returns:
 *   - loading:   true while the request is in flight
 *   - available: true only when an active FRB snapshot exists AND a match was
 *                found for the current filters
 *   - byMetric:  { <onpoint_metric_key>: { company, low, median, high, grade,
 *                                          verdict, higher_is_better, label,
 *                                          band_percentile } }
 *   - meta:      { peer_score, sector, ind_cd, region, size_cd, pub_year,
 *                  region_label, ratios_scored, ratios_above_median, notice }
 *
 * The benchmarking backend maps each FRB ratio to one or more OnPoint metric
 * registry keys. This hook flattens that mapping so callers can look up peer
 * bands by whichever metric key they render (currentRatio, dso, cc_dso, etc).
 *
 * Filters can be supplied by the caller; when omitted, the backend does its
 * own progressive fallback (Size -> Region -> Industry) so a partial match
 * still returns a usable band.
 */
export function usePeerBenchmarks({ sector = null, indCd = null, region = null, sizeCd = null, pubYear = null, enabled = true } = {}) {
  const [state, setState] = useState({ loading: enabled, available: false, byMetric: {}, meta: null });

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, available: false, byMetric: {}, meta: null });
      return () => {};
    }
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");
    const params = new URLSearchParams();
    if (sector !== null && sector !== undefined) params.set("sector", String(sector));
    if (indCd) params.set("indCd", String(indCd));
    if (region !== null && region !== undefined) params.set("region", String(region));
    if (sizeCd !== null && sizeCd !== undefined) params.set("sizeCd", String(sizeCd));
    if (pubYear !== null && pubYear !== undefined) params.set("pubYear", String(pubYear));

    const url = `${backend}/api/benchmarking/comparison${params.toString() ? `?${params}` : ""}`;

    let cancelled = false;
    axios.get(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (cancelled) return;
        const rows = r.data?.rows || [];
        const byMetric = {};
        for (const row of rows) {
          const keys = row.mapped_metric_keys || [];
          for (const k of keys) {
            byMetric[k] = {
              company: row.company,
              low: row.low,
              median: row.median,
              high: row.high,
              unit: row.unit,
              grade: row.grade,
              verdict: row.verdict,
              higher_is_better: row.higher_is_better,
              label: row.label,
              band_percentile: row.band_percentile,
            };
          }
        }
        setState({
          loading: false,
          available: Boolean(r.data?.match && rows.length),
          byMetric,
          meta: {
            peer_score: r.data?.peer_score,
            ratios_scored: r.data?.ratios_scored,
            ratios_above_median: r.data?.ratios_above_median,
            region_label: r.data?.region_label,
            match: r.data?.match,
            notice: r.data?.notice,
            snapshot_id: r.data?.snapshot_id,
          },
        });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, available: false, byMetric: {}, meta: null });
      });

    return () => { cancelled = true; };
  }, [sector, indCd, region, sizeCd, pubYear, enabled]);

  return state;
}
